<template>
  <div class="modal fade" tabindex="-1" data-backdrop="static">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <button class="close" type="button" @click="hide">
            <i class="fa fa-times"></i>
          </button>
          <h3>Select Page Preview</h3>
        </div>
        <div class="modal-body">
          <div class="alert alert-info" v-if="!availablePreviews || availablePreviews.length === 0">
            No Adobe previews found. Make sure the issue has at least one Adobe preview fetched.
          </div>
          <div v-else>
            <div class="preview-selector" v-if="availablePreviews.length > 1">
              <label for="preview-select">Select Preview:</label>
              <select 
                id="preview-select" 
                class="form-control" 
                v-model="selectedPreviewId"
                @change="onPreviewChange"
              >
                <option 
                  v-for="preview in availablePreviews" 
                  :key="preview.id" 
                  :value="preview.id"
                >
                  {{ getPreviewDisplayName(preview) }}
                </option>
              </select>
            </div>
            <div class="preview-title" v-else-if="availablePreviews.length === 1">
              <strong>Preview:</strong> {{ getPreviewDisplayName(availablePreviews[0]) }}
            </div>
            
            <div class="thumbnail-grid">
              <div
                class="thumbnail-item clear-option"
                :class="{ selected: selectedFilename === null }"
                @click="clearSelection"
              >
                <div class="clear-icon">
                  <i class="fa fa-times-circle"></i>
                </div>
                <div class="thumbnail-label">Clear Preview</div>
              </div>
              <div
                class="thumbnail-item"
                v-for="thumbnail in availableThumbnails"
                :key="thumbnail.filename"
                :class="{ selected: selectedFilename === thumbnail.filename }"
                @click="selectThumbnail(thumbnail.filename)"
              >
                <img :src="thumbnail.url" :alt="thumbnail.filename" />
                <div class="thumbnail-label">{{ thumbnail.label }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-link text-muted" @click="hide" type="button" :disabled="saving">
            Cancel
          </button>
          <button class="btn btn-primary" @click="save" :disabled="saving">
            <span v-if="saving">
              <i class="fa fa-circle-o-notch fa-spin"></i> Saving...
            </span>
            <span v-else>Save Selection</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import httpClient from '@/api/httpClient'

  export default {
    props: {
      page: {
        type: Object,
        required: true
      }
    },

    data() {
      return {
        isVisible: false,
        selectedFilename: null,
        selectedPreviewId: null,
        availablePreviews: [],
        availableThumbnails: [],
        saving: false
      }
    },

    watch: {
      isVisible(visible) {
        if (visible) {
          this.loadPreviews()
          jQuery(this.$el).modal({show: true})
        } else {
          jQuery(this.$el).modal('hide')
        }
      }
    },

    methods: {
      show() {
        this.selectedFilename = this.page.preview_image_filename
        this.selectedPreviewId = this.page.adobe_preview_id
        this.isVisible = true
      },

      hide() {
        this.isVisible = false
      },

      async loadPreviews() {
        try {
          // Get issue data with all adobe_previews
          const response = await httpClient.get(`/api/internal/issues/${this.page.issue.id}`)
          const issue = response.data.issue
          
          this.availablePreviews = issue.adobe_previews || []
          
          if (this.availablePreviews.length > 0) {
            // Default to the page's current preview, or the first one
            if (this.selectedPreviewId) {
              const selectedPreview = this.availablePreviews.find(p => p.id === this.selectedPreviewId)
              if (selectedPreview) {
                this.selectedPreviewId = selectedPreview.id
              } else {
                this.selectedPreviewId = this.availablePreviews[0].id
              }
            } else {
              this.selectedPreviewId = this.availablePreviews[0].id
            }
            
            await this.loadThumbnailsForPreview(this.selectedPreviewId)
          }
        } catch (error) {
          console.error('Failed to load previews:', error)
          this.availablePreviews = []
        }
      },

      async onPreviewChange() {
        await this.loadThumbnailsForPreview(this.selectedPreviewId)
        // Clear the selected filename when switching previews
        this.selectedFilename = null
      },

      async loadThumbnailsForPreview(previewId) {
        try {
          const response = await httpClient.get(`/api/internal/adobe_previews/${previewId}/thumbnails`)
          const data = response.data
          
          this.availableThumbnails = data.thumbnails.map((thumb, index) => ({
            filename: thumb.filename,
            label: `Page ${index + 1}`,
            url: thumb.url
          }))
        } catch (error) {
          console.error('Failed to load thumbnails:', error)
          this.availableThumbnails = []
        }
      },

      selectThumbnail(filename) {
        this.selectedFilename = filename
      },

      clearSelection() {
        this.selectedFilename = null
        this.selectedPreviewId = null
      },

      getPreviewDisplayName(preview) {
        const title = preview.preview_title || 'Untitled Preview'
        const date = new Date(preview.created_at)
        const formattedDate = date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric', 
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        })
        return `${title} (${formattedDate})`
      },

      async save() {
        this.saving = true
        try {
          this.$emit('save', {
            preview_image_filename: this.selectedFilename,
            adobe_preview_id: this.selectedPreviewId
          })
          await new Promise(resolve => setTimeout(resolve, 500))
          this.hide()
        } finally {
          this.saving = false
        }
      }
    }
  }
</script>

<style scoped>
  .preview-selector {
    margin-bottom: 1.5rem;
    padding: 1rem;
    background: #f8f9fa;
    border-radius: 4px;
  }

  .preview-selector label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 600;
    color: #333;
  }

  .preview-selector select {
    width: 100%;
  }

  .preview-title {
    margin-bottom: 1rem;
    padding: 0.75rem;
    background: #f8f9fa;
    border-radius: 4px;
  }

  .preview-title strong {
    color: #333;
  }

  .thumbnail-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 1rem;
    max-height: 60vh;
    overflow-y: auto;
    padding: 0.5rem;
  }

  .thumbnail-item {
    cursor: pointer;
    border: 3px solid transparent;
    border-radius: 4px;
    padding: 0.5rem;
    transition: all 0.2s;
    background: #f8f9fa;
  }

  .thumbnail-item.clear-option {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: 150px;
    background: #fff;
    border: 3px dashed #ddd;
  }

  .thumbnail-item.clear-option:hover {
    border-color: #999;
    background: #f8f9fa;
  }

  .thumbnail-item.clear-option.selected {
    border-color: #d9534f;
    border-style: solid;
    background: #ffe6e6;
  }

  .clear-icon {
    font-size: 3rem;
    color: #999;
    margin-bottom: 0.5rem;
  }

  .thumbnail-item.clear-option.selected .clear-icon {
    color: #d9534f;
  }

  .thumbnail-item:hover {
    border-color: #ccc;
    background: #fff;
  }

  .thumbnail-item.selected {
    border-color: #337ab7;
    background: #e7f3ff;
  }

  .thumbnail-item img {
    width: 100%;
    height: auto;
    display: block;
    border-radius: 2px;
  }

  .thumbnail-label {
    text-align: center;
    margin-top: 0.5rem;
    font-size: 0.9rem;
    font-weight: 500;
    color: #333;
  }
</style>
